<script setup>
// Menu Meta -> Historico (id 1 + e-mails MetaAccess): todas as campanhas de
// disparo de TODAS as caixas, mais recentes primeiro, 20 por pagina com
// paginacao, e filtro opcional por caixa de entrada.
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxDispatchesAPI from 'dashboard/api/inboxDispatches';
import DispatchItemsReport from '../settings/inbox/components/DispatchItemsReport.vue';

const store = useStore();
const inboxesGetter = useMapGetter('inboxes/getInboxes');

const campaigns = ref([]);
const expandedId = ref(null);
const total = ref(0);
const page = ref(1);
const perPage = ref(20);
const inboxFilter = ref('');
const isLoading = ref(false);
const loadError = ref('');

const cloudInboxes = computed(() =>
  (inboxesGetter.value || []).filter(
    i => i.channel_type === 'Channel::Whatsapp' && i.provider === 'whatsapp_cloud'
  )
);

const totalPages = computed(() =>
  Math.max(1, Math.ceil(total.value / perPage.value))
);

const STATUS_LABELS = {
  queued: 'Na fila',
  running: 'Disparando',
  paused: 'Pausada',
  completed: 'Concluída',
};

const STATUS_CLASSES = {
  queued: 'bg-n-slate-3 text-n-slate-12',
  running: 'bg-n-amber-3 text-n-amber-12',
  paused: 'bg-n-ruby-3 text-n-ruby-12',
  completed: 'bg-n-teal-3 text-n-teal-12',
};

const fetchHistory = async targetPage => {
  isLoading.value = true;
  loadError.value = '';
  try {
    const { data } = await InboxDispatchesAPI.history(
      targetPage,
      inboxFilter.value
    );
    campaigns.value = data.data || [];
    expandedId.value = null;
    total.value = data.total || 0;
    page.value = data.page || targetPage;
    perPage.value = data.per_page || 20;
  } catch (e) {
    loadError.value = e?.response?.data?.error || e.message;
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = () => fetchHistory(1);

const formatDate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} ${d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

onMounted(() => {
  store.dispatch('inboxes/get');
  fetchHistory(1);
});

const selectClass =
  'h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 outline-none !mb-0 min-w-72';
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-surface-1 p-8">
    <div class="max-w-7xl w-full mx-auto">
      <div class="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 class="text-heading-1 text-n-slate-12 mb-1">Meta — Histórico</h1>
          <p class="text-n-slate-11 text-sm">
            Campanhas de disparo de todas as caixas, das mais recentes para as
            mais antigas.
          </p>
        </div>
        <div class="flex gap-2 shrink-0 items-center">
          <select
            v-model="inboxFilter"
            :class="selectClass"
            @change="onFilterChange"
          >
            <option value="">Todas as caixas</option>
            <option v-for="box in cloudInboxes" :key="box.id" :value="box.id">
              {{ box.name }} ({{ box.phone_number }})
            </option>
          </select>
          <NextButton
            variant="outline"
            color="slate"
            label="Atualizar"
            :is-loading="isLoading"
            @click="fetchHistory(page)"
          />
        </div>
      </div>

      <p v-if="loadError" class="text-sm text-n-ruby-11 mb-4">
        {{ loadError }}
      </p>

      <div
        v-if="isLoading && !campaigns.length"
        class="text-sm text-n-slate-11 py-8 text-center"
      >
        Carregando histórico…
      </div>

      <div
        v-else-if="!campaigns.length"
        class="text-sm text-n-slate-11 py-8 text-center border border-n-weak rounded-lg"
      >
        Nenhum disparo encontrado.
      </div>

      <div v-else class="overflow-x-auto rounded-lg border border-n-weak">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-weak text-left text-n-slate-11">
              <th class="px-3 py-2 font-medium">Campanha</th>
              <th class="px-3 py-2 font-medium">Caixa</th>
              <th class="px-3 py-2 font-medium">Template</th>
              <th class="px-3 py-2 font-medium">Criada em</th>
              <th class="px-3 py-2 font-medium">Status</th>
              <th class="px-3 py-2 font-medium">Total</th>
              <th class="px-3 py-2 font-medium">Disparados</th>
              <th class="px-3 py-2 font-medium">Entregues</th>
              <th class="px-3 py-2 font-medium">Lidos</th>
              <th class="px-3 py-2 font-medium">Erros</th>
              <th class="px-3 py-2 font-medium">Pendentes</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="c in campaigns" :key="c.id">
            <tr
              class="border-b border-n-weak last:border-b-0 cursor-pointer hover:bg-n-alpha-black1"
              @click="expandedId = expandedId === c.id ? null : c.id"
            >
              <td class="px-3 py-2 font-medium text-n-slate-12">
                {{ c.name }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">{{ c.inbox_name }}</td>
              <td class="px-3 py-2 font-mono text-xs text-n-slate-11">
                {{ c.template_name }}
              </td>
              <td class="px-3 py-2 text-n-slate-11">
                {{ formatDate(c.created_at) }}
              </td>
              <td class="px-3 py-2">
                <span
                  class="inline-flex px-2 py-0.5 rounded text-xs font-medium"
                  :class="STATUS_CLASSES[c.status] || 'bg-n-slate-3 text-n-slate-12'"
                >
                  {{ STATUS_LABELS[c.status] || c.status }}
                </span>
              </td>
              <td class="px-3 py-2 text-n-slate-12">{{ c.counters.total }}</td>
              <td class="px-3 py-2 text-n-teal-11">
                {{ c.counters.disparados }}
              </td>
              <td class="px-3 py-2 text-n-teal-11">
                {{ c.counters.entregues }}
              </td>
              <td class="px-3 py-2 text-n-blue-11">{{ c.counters.lidos }}</td>
              <td class="px-3 py-2 text-n-ruby-11">{{ c.counters.erros }}</td>
              <td class="px-3 py-2 text-n-slate-11">
                {{ c.counters.pendentes }}
              </td>
            </tr>
            <tr v-if="expandedId === c.id" class="border-b border-n-weak">
              <td colspan="11" class="px-3 py-3 bg-n-alpha-black1">
                <DispatchItemsReport :inbox-id="c.inbox_id" :campaign="c" />
              </td>
            </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-if="campaigns.length" class="flex items-center gap-2 mt-3">
        <NextButton
          size="xs"
          variant="outline"
          color="slate"
          label="Anterior"
          :disabled="page <= 1 || isLoading"
          @click="fetchHistory(page - 1)"
        />
        <span class="text-xs text-n-slate-11">
          Página {{ page }} de {{ totalPages }} ({{ total }} campanhas)
        </span>
        <NextButton
          size="xs"
          variant="outline"
          color="slate"
          label="Próxima"
          :disabled="page >= totalPages || isLoading"
          @click="fetchHistory(page + 1)"
        />
      </div>
    </div>
  </div>
</template>
